import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "./components/Sidebar";
import Navbar from "./components/Navbar";
import UserChartPage from "./components/UserChartPage";
import {
  FiUsers,
  FiUserPlus,
  FiZap,
  FiMoon,
  FiDollarSign,
} from "react-icons/fi";

const baseUrl = process.env.REACT_APP_API_BASE_URL;

const StatCard = ({ label, value, icon: Icon, iconBg, iconColor, border, onClick, subtitle }) => (
  <div
    onClick={onClick}
    className={`bg-white rounded-xl shadow-sm border-l-4 ${border} p-5 flex items-center justify-between gap-4 transition ${
      onClick ? "cursor-pointer hover:shadow-md hover:translate-y-[-1px]" : ""
    }`}
  >
    <div className="flex items-center gap-4">
      <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${iconBg}`}>
        <Icon size={22} className={iconColor} />
      </div>
      <div>
        <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">{label}</p>
        <p className="text-2xl font-bold text-gray-800">{value ?? "—"}</p>
        {subtitle && <p className="text-[11px] text-green-600 font-medium mt-0.5">{subtitle}</p>}
      </div>
    </div>
  </div>
);

const Dashboard = () => {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(`${baseUrl}/auth/dashboard`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        const result = await res.json();
        if (res.ok && result.status) {
          setDashboardData(result.data);
        } else {
          setError(result.message || "Failed to fetch dashboard data");
        }
      } catch (err) {
        setError("Network error");
      } finally {
        setLoading(false);
      }
    };
    fetchDashboardData();
  }, []);

  return (
    <div className="flex bg-gray-100 min-h-screen">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Navbar />
        <div className="p-5 sm:p-8">

          {/* Page heading */}
          <div className="mb-6">
            <h3 className="text-2xl font-bold text-gray-800">Dashboard</h3>
            <p className="text-sm text-gray-400 mt-0.5">Welcome back, Admin</p>
          </div>

          {loading ? (
            /* Skeleton loader */
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 mb-6">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="bg-white rounded-xl p-5 animate-pulse h-24" />
              ))}
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 text-red-600 rounded-xl p-5 mb-6">
              {error}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 mb-6">
              <StatCard
                label="Total Users"
                value={dashboardData.totalUser?.toLocaleString()}
                icon={FiUsers}
                iconBg="bg-[#ffffcc]"
                iconColor="text-[#999900]"
                border="border-[#ffff00]"
                onClick={() => navigate("/user")}
              />
              <StatCard
                label="Today New Users"
                value={dashboardData.todayCreatedUser?.toLocaleString()}
                icon={FiUserPlus}
                iconBg="bg-green-100"
                iconColor="text-green-600"
                border="border-green-500"
                subtitle="Click to view full user details →"
                onClick={() => navigate("/today-users")}
              />
              <StatCard
                label="Active Miners"
                value={dashboardData.totalMiningActiveCount?.toLocaleString()}
                icon={FiZap}
                iconBg="bg-yellow-100"
                iconColor="text-yellow-600"
                border="border-yellow-500"
              />
              <StatCard
                label="Inactive Miners"
                value={dashboardData.totalMiningInactiveCount?.toLocaleString()}
                icon={FiMoon}
                iconBg="bg-gray-100"
                iconColor="text-gray-500"
                border="border-gray-400"
              />
              <StatCard
                label="Total Coins Mined"
                value={parseFloat(dashboardData.totalCoinMin || 0).toFixed(4)}
                icon={FiDollarSign}
                iconBg="bg-purple-100"
                iconColor="text-purple-600"
                border="border-purple-500"
              />
            </div>
          )}

          {/* Chart */}
          <UserChartPage />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
